import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import {
    Action,
    NgxsOnInit,
    Selector,
    State,
    StateContext,
} from '@ngxs/store';
import { of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { uid } from 'uid';

import {
    AddContactsListAction,
    ChangeCurrentContactsListAction,
    CloseContactsListAction,
} from '~/app/core/state/contacts-list/contacts-list.actions';
import { Workspace } from '~/app/shared/types/workspace.type';

import {
    AddWorkspaceAction,
    ChangeActiveWorkspaceAction,
    CloseWorkspaceAction,
    ResetContactsWorkspaceAction,
} from './contacts-workspace.actions';

export type ContactsWorkspaceStateModel = {
    currentWorkspace: string,
    workspaces: Record<string, Workspace>,
}

const defaults = {
    currentWorkspace: '',
    workspaces: {},
};

@State<ContactsWorkspaceStateModel>({
    name: 'contactsWorkspaces',
    defaults,
})
@Injectable()
export class ContactsWorkspaceState implements NgxsOnInit {
    private transloco = inject(TranslocoService);


    @Selector()
    static workspaces(state: ContactsWorkspaceStateModel) {
        return Object.values(state.workspaces);
    }

    // TODO: Uncomment and fix the selector
    // @Selector([ContactsListState])
    // static workspaces(state: ContactsWorkspaceStateModel, listState: ContactsListStateModel) {
    //     return Object.values(state.workspaces)
    //         .map((item) => ({
    //             ...item,
    //             ...(listState?.lists[item.key] ? { number: listState.lists[item.key].filtersCount } : {}),
    //         }));
    // }

    @Selector()
    static currentWorkspace(state: ContactsWorkspaceStateModel) {
        return state.currentWorkspace;
    }

    @Selector()
    static workspacesCount(state: ContactsWorkspaceStateModel): number {
        return Object.keys(state.workspaces).length;
    }

    @Action(AddWorkspaceAction)
    addWorkspace({ getState, patchState, dispatch }: StateContext<ContactsWorkspaceStateModel>, action: AddWorkspaceAction) {
        const state = getState();
        const key: string = uid();
        const workspaceName$ = action.payload.label ? of(action.payload.label) : of('workspace.default');

        return workspaceName$.pipe(
            tap((label: string) => {
                patchState({
                    currentWorkspace: key,
                    workspaces: {
                        ...state.workspaces,
                        [key]: {
                            key,
                            label,
                            translatable: !!action.payload.label,
                        },
                    },
                });

                dispatch(new AddContactsListAction(key));
            }),
        );
    }

    @Action(ChangeActiveWorkspaceAction)
    changeActiveWorkspace({ getState, patchState, dispatch }: StateContext<ContactsWorkspaceStateModel>, action: ChangeActiveWorkspaceAction) {
        const state = getState();

        if (!state.workspaces[action.key]) { return; }

        patchState({ currentWorkspace: action.key });

        dispatch(new ChangeCurrentContactsListAction(action.key));
    }

    @Action(CloseWorkspaceAction)
    closeWorkspace({ getState, patchState, dispatch }: StateContext<ContactsWorkspaceStateModel>, action: CloseWorkspaceAction) {
        const state = getState();

        if (!state.workspaces[action.key]) { return; }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [action.key]: workspace, ...rest } = state.workspaces;

        let { currentWorkspace } = state;

        if (state.currentWorkspace === action.key) {
            const index = Object.keys(state.workspaces).indexOf(action.key);

            if (index === Object.keys(state.workspaces).length - 1) {
                currentWorkspace = Object.keys(state.workspaces)[Object.keys(state.workspaces).length - 2];
            } else {
                currentWorkspace = Object.keys(state.workspaces)[index + 1];
            }

            if (Object.keys(rest).length > 0) {
                dispatch(new ChangeActiveWorkspaceAction(currentWorkspace));
            }
        }

        patchState({ workspaces: { ...rest } });

        dispatch(new CloseContactsListAction(action.key));

        // Add new workspace
        if (Object.keys(rest).length === 0) {
            dispatch(new AddWorkspaceAction({}));
        }
    }

    @Action(ResetContactsWorkspaceAction)
    resetContactsWorkspace({ getState, dispatch }: StateContext<ContactsWorkspaceStateModel>) {
        const state = getState();
        Object.keys(state.workspaces).forEach((key) => {
            dispatch(new CloseWorkspaceAction(key));
        });
    }

    ngxsOnInit({ getState, dispatch }: StateContext<ContactsWorkspaceStateModel>) {
        const state = getState();

        if (Object.keys(state.workspaces).length === 0) {
            dispatch(new AddWorkspaceAction({}));
        }
    }
}
